import type { PreloadTag } from '../api/preload'
import type { FeedPrefs, Tag, ViewerGender, ViewerOrientation } from '../types'

export const GENDER_OPTIONS: { value: ViewerGender; label: string; symbol: string }[] = [
  { value: 'male', label: 'Male', symbol: '♂' },
  { value: 'female', label: 'Female', symbol: '♀' },
  { value: 'all', label: 'All', symbol: '⚥' },
]

export const ORIENTATION_OPTIONS: { value: ViewerOrientation; label: string; symbol: string }[] = [
  { value: 'straight', label: 'Straight', symbol: '⚤' },
  { value: 'gay', label: 'Gay', symbol: '⚣' },
  { value: 'all', label: 'All', symbol: '✦' },
]

export function feedPrefsReady(prefs: FeedPrefs): boolean {
  return prefs.gender !== null && prefs.orientation !== null
}

export function orientationSymbol(prefs: FeedPrefs): string {
  if (prefs.orientation === 'gay' && prefs.gender === 'female') return '⚢'
  const opt = ORIENTATION_OPTIONS.find((o) => o.value === prefs.orientation)
  return opt?.symbol ?? '✦'
}

export function formatSexualitySummary(prefs: FeedPrefs): string | null {
  if (!feedPrefsReady(prefs)) return null
  const g = GENDER_OPTIONS.find((o) => o.value === prefs.gender)!
  const o = ORIENTATION_OPTIONS.find((opt) => opt.value === prefs.orientation)!
  return `${g.symbol} ${g.label} · ${orientationSymbol(prefs)} ${o.label}`
}

/** Scope hashtag for viewer prefs — Bluesky needs simple single tags. */
export function scopeHashtag(prefs: FeedPrefs): string | null {
  const { gender, orientation } = prefs
  if (!gender || !orientation) return null
  if (gender === 'all' && orientation === 'all') return null

  if (orientation === 'gay') {
    if (gender === 'female') return '#lesbian'
    return '#gay'
  }
  if (orientation === 'straight') {
    if (gender === 'male') return '#woman'
    if (gender === 'female') return '#man'
    return '#nsfw'
  }
  if (gender === 'male') return '#men'
  if (gender === 'female') return '#women'
  return '#nsfw'
}

function scopeTag(prefs: FeedPrefs): PreloadTag | null {
  const query = scopeHashtag(prefs)
  if (!query) return null
  return { id: '_pref_scope', label: 'FOR YOU', query }
}

export function buildFeedTags(chosen: Tag[], prefs: FeedPrefs): PreloadTag[] {
  const scope = scopeTag(prefs)
  const tags = chosen.map((t) => ({ id: t.id, label: t.label, query: t.query }))
  return scope ? [scope, ...tags] : tags
}